import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import ExportButton from "../../components/ExportButton";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UserCheck, UserX, KeyRound } from "lucide-react";
import PasswordDialog from "./PasswordDialog";

const EMPTY = { name: "", email: "", password: "", employee_code: "", role: "employee", employee_category: "permanent", mobile: "", designation: "", department: "", manager_id: "", partner_id: "", date_of_joining: "", pan: "", aadhar: "", status: "active", ctc_basic: 0, ctc_hra: 0, ctc_special: 0, ctc_other: 0, annual_paid_leaves: 12, annual_unpaid_leaves: 0 };

export default function People() {
  const { user } = useAuth();
  const canManage = ["partner","admin","manager"].includes(user?.role);
  const [pwUser, setPwUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const canApprove = ["partner", "admin"].includes(user?.role);

  const load = () => Promise.all([
    api.get("/users").then((r) => setUsers(r.data)),
    canApprove ? api.get("/users/pending").then((r) => setPending(r.data)).catch(() => setPending([])) : Promise.resolve(),
  ]);
  useEffect(() => { load(); }, []);

  const handleApprovalAction = async (uid, action) => {
    const labels = { review: "send to Partner for approval", approve: "approve and activate", reject: "reject" };
    const remarks = window.prompt(`Remarks (optional) — ${labels[action]}:`, "") ?? "";
    try {
      await api.post(`/users/${uid}/approve`, { action, remarks });
      toast.success(action === "approve" ? "User activated" : action === "review" ? "Forwarded to Partner" : "Rejected");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const partners = users.filter((u) => u.role === "partner");

  const openCreate = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (u) => { setForm({ ...EMPTY, ...u, password: "" }); setEditId(u.id); setOpen(true); };

  const remove = async (id) => {
    if (!window.confirm("Permanently delete this employee?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) {
        // remove fields that are not in UserUpdateIn
        const { password, email, ...payload } = form;
        // convert numeric fields
        payload.ctc_basic = Number(payload.ctc_basic || 0);
        payload.ctc_hra = Number(payload.ctc_hra || 0);
        payload.ctc_special = Number(payload.ctc_special || 0);
        payload.ctc_other = Number(payload.ctc_other || 0);
        payload.annual_paid_leaves = Number(payload.annual_paid_leaves || 0);
        payload.annual_unpaid_leaves = Number(payload.annual_unpaid_leaves || 0);
        await api.patch(`/users/${editId}`, payload);
        toast.success("Updated");
      } else {
        if (!form.password) { toast.error("Password required"); setBusy(false); return; }
        await api.post("/users", { name: form.name, email: form.email, password: form.password, role: form.role, mobile: form.mobile, employee_category: form.employee_category });
        toast.success("Employee created");
      }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const roleColor = (r) => ({
    partner: "border-[#C2A671] text-[#946f30] bg-[#fdf8ee]",
    admin: "border-blue-200 text-blue-700 bg-blue-50",
    manager: "border-amber-200 text-amber-700 bg-amber-50",
    employee: "border-slate-200 text-slate-700 bg-slate-50",
    accounts: "border-emerald-200 text-emerald-700 bg-emerald-50",
  }[r] || "border-slate-200");

  return (
    <div className="space-y-6 fade-in" data-testid="people-page">
      <PasswordDialog open={!!pwUser} target={pwUser} onClose={() => setPwUser(null)} />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">People</div>
          <h1 className="font-sans text-4xl mt-2">Employee directory</h1>
        </div>
        <div className="flex items-center gap-3">
          {canManage && <ExportButton path="/exports/users" filename="people.csv" testId="people-export"/>}
          {canManage && <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-person-btn"><Plus className="h-4 w-4 mr-2"/>Add Employee</Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-sans text-2xl">{editId ? "Edit Employee" : "New Employee"}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-4">
                <div><Label className="eyebrow">Name</Label><Input required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="mt-2 rounded-sm" data-testid="person-name"/></div>
                <div><Label className="eyebrow">Email</Label><Input required type="email" disabled={!!editId} value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="mt-2 rounded-sm"/></div>
                {!editId && <div><Label className="eyebrow">Password</Label><Input type="password" required minLength={6} value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} className="mt-2 rounded-sm"/></div>}
                <div><Label className="eyebrow">Mobile</Label><Input value={form.mobile || ""} onChange={(e)=>setForm({...form, mobile: e.target.value})} className="mt-2 rounded-sm"/></div>
                <div><Label className="eyebrow">Employee Code</Label><Input value={form.employee_code || ""} onChange={(e)=>setForm({...form, employee_code: e.target.value})} disabled={!editId} placeholder={editId ? "" : "Auto-assigned on create"} className="mt-2 rounded-sm font-mono" data-testid="person-employee-code"/></div>
                <div><Label className="eyebrow">Type</Label>
                  <Select value={form.employee_category || "permanent"} onValueChange={(v)=>setForm({...form, employee_category: v})} disabled={!canApprove}>
                    <SelectTrigger className="mt-2 rounded-sm" data-testid="person-type"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="permanent">Employee</SelectItem>
                      <SelectItem value="article_trainee">Article Trainee</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="eyebrow">Role (portal access)</Label>
                  <Select value={form.role} onValueChange={(v)=>setForm({...form, role: v})} disabled={!canApprove && !!editId}>
                    <SelectTrigger className="mt-2 rounded-sm" data-testid="person-role"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="accounts">Accounts</SelectItem>
                      <SelectItem value="admin">Admin / HR</SelectItem>
                      <SelectItem value="partner">Partner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="eyebrow">Designation</Label><Input value={form.designation || ""} onChange={(e)=>setForm({...form, designation: e.target.value})} className="mt-2 rounded-sm"/></div>
                <div><Label className="eyebrow">Department</Label><Input value={form.department || ""} onChange={(e)=>setForm({...form, department: e.target.value})} className="mt-2 rounded-sm"/></div>
                <div><Label className="eyebrow">Tagged Partner</Label>
                  <Select value={form.partner_id || "_none"} onValueChange={(v)=>setForm({...form, partner_id: v === "_none" ? "" : v})}>
                    <SelectTrigger className="mt-2 rounded-sm" data-testid="person-partner-select"><SelectValue placeholder="Select partner"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">— None —</SelectItem>
                      {partners.map((p)=><SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="eyebrow">Date of Joining</Label><Input type="date" value={form.date_of_joining || ""} onChange={(e)=>setForm({...form, date_of_joining: e.target.value})} className="mt-2 rounded-sm"/></div>
                <div><Label className="eyebrow">PAN</Label><Input value={form.pan || ""} onChange={(e)=>setForm({...form, pan: e.target.value})} className="mt-2 rounded-sm"/></div>
                <div><Label className="eyebrow">Aadhar</Label><Input value={form.aadhar || ""} onChange={(e)=>setForm({...form, aadhar: e.target.value})} className="mt-2 rounded-sm"/></div>
                <div><Label className="eyebrow">Status</Label>
                  <Select value={form.status} onValueChange={(v)=>setForm({...form, status: v})}>
                    <SelectTrigger className="mt-2 rounded-sm"><SelectValue/></SelectTrigger>
                    <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 border-t border-slate-200 pt-4 mt-2">
                  <div className="eyebrow mb-3">Leave Allowance (per year)</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Paid Leaves</Label><Input type="number" value={form.annual_paid_leaves ?? 12} onChange={(e)=>setForm({...form, annual_paid_leaves: e.target.value})} className="mt-1 rounded-sm" data-testid="person-paid-leaves"/></div>
                    <div><Label className="text-xs">Unpaid Leaves</Label><Input type="number" value={form.annual_unpaid_leaves ?? 0} onChange={(e)=>setForm({...form, annual_unpaid_leaves: e.target.value})} className="mt-1 rounded-sm"/></div>
                  </div>
                </div>
                <div className="col-span-2 border-t border-slate-200 pt-4 mt-2">
                  <div className="eyebrow mb-3">CTC Structure (monthly ₹)</div>
                  <div className="grid grid-cols-4 gap-3">
                    <div><Label className="text-xs">Basic</Label><Input type="number" value={form.ctc_basic || 0} onChange={(e)=>setForm({...form, ctc_basic: e.target.value})} className="mt-1 rounded-sm"/></div>
                    <div><Label className="text-xs">HRA</Label><Input type="number" value={form.ctc_hra || 0} onChange={(e)=>setForm({...form, ctc_hra: e.target.value})} className="mt-1 rounded-sm"/></div>
                    <div><Label className="text-xs">Special</Label><Input type="number" value={form.ctc_special || 0} onChange={(e)=>setForm({...form, ctc_special: e.target.value})} className="mt-1 rounded-sm"/></div>
                    <div><Label className="text-xs">Other</Label><Input type="number" value={form.ctc_other || 0} onChange={(e)=>setForm({...form, ctc_other: e.target.value})} className="mt-1 rounded-sm"/></div>
                  </div>
                </div>
                <DialogFooter className="col-span-2"><Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="person-save-btn">{busy ? "Saving..." : "Save"}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>}
        </div>
      </div>

      {canApprove && pending.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-sm overflow-x-auto" data-testid="pending-users-block">
          <div className="px-6 py-3 border-b border-amber-300 flex items-center justify-between">
            <div className="eyebrow text-amber-900">Pending User Approvals ({pending.length})</div>
            <div className="text-xs text-amber-800">
              {user?.role === "admin" ? "HR/Admin: review → forward to Partner" : "Partner: review and approve / reject"}
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-amber-100 text-amber-800 uppercase tracking-[0.18em] text-xs">
              <tr><th className="text-left p-3">Code</th><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Role</th><th className="text-left p-3">Status</th><th className="text-left p-3">Registered On</th><th className="p-3 text-right">Action</th></tr>
            </thead>
            <tbody className="divide-y divide-amber-200" data-testid="pending-users-table">
              {pending.map((u) => (
                <tr key={u.id} className="hover:bg-amber-100/50">
                  <td className="p-3 font-mono text-xs">{u.employee_code}</td>
                  <td className="p-3 font-medium">{u.name}</td>
                  <td className="p-3 text-slate-700">{u.email}</td>
                  <td className="p-3 text-xs uppercase tracking-[0.15em]">{u.role}</td>
                  <td className="p-3 text-xs uppercase tracking-[0.15em] text-amber-800">{u.status === "pending_review" ? "Awaiting HR/Admin" : "Awaiting Partner"}</td>
                  <td className="p-3 font-mono text-xs">{(u.created_at || "").slice(0, 10)}</td>
                  <td className="p-3 text-right whitespace-nowrap space-x-1">
                    {u.status === "pending_review" && user?.role === "admin" && (
                      <Button size="sm" onClick={() => handleApprovalAction(u.id, "review")} className="bg-blue-600 hover:bg-blue-700 rounded-sm text-xs" data-testid={`user-review-${u.id}`}><UserCheck className="h-3.5 w-3.5 mr-1"/>Forward to Partner</Button>
                    )}
                    {(u.status === "pending_partner" || (u.status === "pending_review" && user?.role === "partner")) && (
                      <Button size="sm" onClick={() => handleApprovalAction(u.id, "approve")} className="bg-emerald-600 hover:bg-emerald-700 rounded-sm text-xs" data-testid={`user-approve-${u.id}`}><UserCheck className="h-3.5 w-3.5 mr-1"/>Approve</Button>
                    )}
                    <Button size="sm" onClick={() => handleApprovalAction(u.id, "reject")} variant="outline" className="rounded-sm border-red-300 text-red-700 hover:bg-red-50 text-xs" data-testid={`user-reject-${u.id}`}><UserX className="h-3.5 w-3.5 mr-1"/>Reject</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
            <tr><th className="text-left p-4">Code</th><th className="text-left p-4">Name</th><th className="text-left p-4">Email</th><th className="text-left p-4">Role</th><th className="text-left p-4">Type</th><th className="text-left p-4">Designation</th><th className="text-left p-4">Partner</th><th className="text-left p-4">Status</th><th className="p-4"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="people-table">
            {users.map((u) => {
              const partnerName = users.find((p) => p.id === u.partner_id)?.name;
              return (
                <tr key={u.id} className="hover:bg-slate-50">
                  <td className="p-4 font-mono text-xs">{u.employee_code}</td>
                  <td className="p-4 font-medium">{u.name}</td>
                  <td className="p-4 text-slate-600">{u.email}</td>
                  <td className="p-4"><span className={`text-xs uppercase tracking-[0.2em] px-2 py-1 border rounded-full ${roleColor(u.role)}`}>{u.role}</span></td>
                  <td className="p-4" data-testid={`person-type-${u.id}`}>{["client", "partner"].includes(u.role) ? "—" : u.employee_category === "article_trainee" ? <span className="text-xs px-2 py-1 border rounded-full bg-amber-50 text-amber-800 border-amber-200">Article Trainee</span> : <span className="text-xs px-2 py-1 border rounded-full bg-slate-50 text-slate-700 border-slate-200">Employee</span>}</td>
                  <td className="p-4 text-slate-600">{u.designation || "—"}</td>
                  <td className="p-4 text-slate-600">{partnerName || "—"}</td>
                  <td className="p-4 text-xs uppercase tracking-[0.2em] text-slate-500">{u.status}</td>
                  <td className="p-4 text-right">{canManage && <div className="inline-flex items-center gap-3"><button onClick={()=>openEdit(u)} className="text-slate-400 hover:text-slate-900" data-testid={`person-edit-${u.id}`}><Pencil className="h-4 w-4"/></button>{(user?.role === "partner" || (user?.role === "admin" && u.role !== "partner")) && <button onClick={()=>setPwUser(u)} title="Reset password" className="text-slate-400 hover:text-slate-900" data-testid={`person-reset-pw-${u.id}`}><KeyRound className="h-4 w-4"/></button>}{user?.role === "partner" && u.id !== user.id && <button onClick={()=>remove(u.id)} className="text-slate-400 hover:text-red-600" data-testid={`person-delete-${u.id}`}><Trash2 className="h-4 w-4"/></button>}</div>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
